import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Target, Loader2, Info, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Diálogo ÚNICO para reposicionar a un cliente en su macro.
 *
 * Reemplaza a:
 *  - "Ajustar Manualmente"
 *  - "Reparar Mesociclo"
 *  - "+ Añadir Mesociclo"
 *  - "Diagnosticar"
 *  - "Resetear Todo"
 *  - "Posición en el Macro"
 *
 * Funciona tanto si el cliente tiene Macro como si solo tiene plan suelto.
 *
 * Lógica de conversión:
 *   • Semana del macro 1-5   →  Meso 1, semana 1-5
 *   • Semana del macro 6-10  →  Meso 2, semana 1-5
 *   • Semana del macro 11-15 →  Meso 3, semana 1-5
 *   • Semana del macro 16-20 →  Meso 4, semana 1-5
 *   • Semana del macro 21-25 →  Meso 5, semana 1-5
 *
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - clientId: string
 *   - clientName: string
 *   - planId?: string           (para clientes sin macro con varios planes)
 *   - currentMesocycle?: number (1-5, opcional, para mostrar estado actual)
 *   - currentWeek?: number      (semana dentro del meso, 1-5)
 *   - currentSession?: number
 *   - sessionsCompleted?: number
 *   - onSuccess?: () => void
 */
const MacroPositionDialog = ({
  open,
  onClose,
  clientId,
  clientName,
  planId = null,
  currentMesocycle = 1,
  currentWeek = 1,
  currentSession = 1,
  sessionsCompleted = 0,
  onSuccess,
}) => {
  // Calcular semana del macro actual (1-25) y sesión total
  const currentMacroWeek = ((currentMesocycle - 1) * 5) + currentWeek;

  const [macroWeek, setMacroWeek] = useState(currentMacroWeek);
  const [session, setSession] = useState(currentSession);
  const [submitting, setSubmitting] = useState(false);

  // Conversión a meso + semana_meso
  const mesoNum = Math.floor((macroWeek - 1) / 5) + 1;
  const weekInMeso = ((macroWeek - 1) % 5) + 1;

  const isValid = macroWeek >= 1 && macroWeek <= 25 && session >= 1;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error('Semana del macro debe estar entre 1 y 25');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        mesocycle: mesoNum,
        week: weekInMeso,
        session: parseInt(session),
        reset_sessions_completed: false,
      };
      if (planId) body.plan_id = planId;
      const response = await axios.post(
        `${API}/admin/clients/${clientId}/move-to-session`,
        body
      );
      toast.success(
        response.data.message ||
          `${clientName} reposicionado en Semana ${macroWeek} (Meso ${mesoNum})`
      );
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reposicionar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !submitting) onClose(); }}>
      <DialogContent
        className="bg-card border-border max-w-md"
        data-testid="macro-position-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-400" />
            Reposicionar a {clientName}
          </DialogTitle>
          <DialogDescription>
            Mueve al cliente a la sesión exacta de su macro. Esto sincroniza el
            mesociclo activo y el plan asignado.
          </DialogDescription>
        </DialogHeader>

        {/* Estado actual */}
        <div className="rounded-lg border border-border bg-background/50 p-3 text-sm space-y-1">
          <div className="flex items-center gap-2 text-muted-foreground font-medium">
            <Info className="h-4 w-4" /> Estado actual
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs mt-2">
            <div>
              <span className="text-muted-foreground">Semana del macro: </span>
              <span className="font-bold text-foreground">{currentMacroWeek} / 25</span>
            </div>
            <div>
              <span className="text-muted-foreground">Mesociclo: </span>
              <span className="font-bold text-foreground">{currentMesocycle} / 5</span>
            </div>
            <div>
              <span className="text-muted-foreground">Sesión: </span>
              <span className="font-bold text-foreground">{currentSession}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Completadas: </span>
              <span className="font-bold text-foreground">{sessionsCompleted}</span>
            </div>
          </div>
        </div>

        {/* Inputs */}
        <div className="space-y-3">
          <div>
            <Label htmlFor="macro-week" className="text-sm">
              Semana del macro (1-25)
            </Label>
            <Input
              id="macro-week"
              type="number"
              min={1}
              max={25}
              value={macroWeek}
              onChange={(e) => setMacroWeek(parseInt(e.target.value) || 1)}
              className="mt-1"
              data-testid="macro-week-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              → Mesociclo {mesoNum} · Semana {weekInMeso} del meso
            </p>
          </div>

          <div>
            <Label htmlFor="session-num" className="text-sm">
              Sesión dentro de esa semana
            </Label>
            <Input
              id="session-num"
              type="number"
              min={1}
              value={session}
              onChange={(e) => setSession(parseInt(e.target.value) || 1)}
              className="mt-1"
              data-testid="session-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              (Si la semana tiene 3 sesiones: 1, 2 o 3)
            </p>
          </div>
        </div>

        {/* Aviso */}
        <div className="flex items-start gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 p-2.5 text-xs text-amber-200">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p>
            Esto NO borra sesiones completadas. Solo cambia dónde está el
            cliente ahora. Útil cuando el sistema se "salta" o cuando quieres
            adelantar/atrasar manualmente.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold"
            data-testid="reposition-confirm-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reposicionando...
              </>
            ) : (
              <>
                <Target className="h-4 w-4 mr-2" /> Reposicionar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MacroPositionDialog;
